/**
 * Attribution Tracking
 *
 * Captures ad click parameters from URL and persists them in localStorage
 * for 90 days to attribute conversions to their source.
 */

import { AttributionData } from './types';
import { warn } from './logger';

const ATTRIBUTION_STORAGE_KEY = '_tablz_attribution';
const ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

interface StoredAttribution {
  data: AttributionData;
  timestamp: number;
}

/**
 * Capture attribution parameters from current URL and store in localStorage
 */
export function captureAttribution(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const urlParams = new URLSearchParams(window.location.search);
    const attribution: AttributionData = {};

    // Ad platform click IDs
    const AD_CLICK_PARAMS = [
      'gclid', // Google Ads
      'gbraid', // Google Ads iOS app (privacy-safe)
      'wbraid', // Google Ads iOS web (privacy-safe)
      'gad_source', // Google Ads source type
      'fbclid', // Facebook / Meta Ads
      'ttclid', // TikTok Ads
      'msclkid', // Microsoft / Bing Ads
      'li_fat_id' // LinkedIn Ads
    ] as const;

    // UTM parameters
    const UTM_PARAMS = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'utm_content'
    ] as const;

    let hasAttribution = false;

    for (const param of [...AD_CLICK_PARAMS, ...UTM_PARAMS]) {
      const value = urlParams.get(param);
      if (value) {
        (attribution as Record<string, string>)[param] = value;
        hasAttribution = true;
      }
    }

    // Store attribution if we captured any parameters
    if (hasAttribution) {
      const stored: StoredAttribution = {
        data: attribution,
        timestamp: Date.now()
      };
      localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(stored));
    }
  }
 catch (error) {
    warn('Failed to capture attribution:', error);
  }
}

/**
 * Get stored attribution data if still valid (within 90 days)
 */
export function getAttribution(): AttributionData | null {
  if (typeof window === 'undefined') {
return null;
}

  try {
    const stored = localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!stored) {
return null;
}

    const attribution: StoredAttribution = JSON.parse(stored);
    const now = Date.now();

    // Check if attribution is still valid
    if (now - attribution.timestamp < ATTRIBUTION_TTL_MS) {
      return attribution.data;
    }

    // Attribution expired, remove it
    localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
    return null;
  }
 catch (error) {
    warn('Failed to retrieve attribution:', error);
    return null;
  }
}

/**
 * Clear stored attribution (for testing purposes)
 */
export function clearAttribution(): void {
  if (typeof window === 'undefined') {
return;
}

  try {
    localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
  }
 catch (error) {
    warn('Failed to clear attribution:', error);
  }
}
